// src/components/HeaderOne.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import query from "jquery";
import { Link, NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { CategoriesServices } from "../services/CategoriesServices";

import CustomerAuthService from "../services/CustomerAuthServices";
import { getCustomerToken } from "../utils/CustomerAuth";

import { getLocations } from "../services/LocationServices";

import { CartServices } from "../services/CartServices";
import { WishlistServices } from "../services/WishlistServices";

const HeaderOne = ({ showCategories = true, showSearchBar = true }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [scroll, setScroll] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("Eng");
  const [selectedCurrency, setSelectedCurrency] = useState("USD");
  const [selectedLocation, setSelectedLocation] = useState("United States");
  const [locations, setLocations] = useState([]);
  const [menuActive, setMenuActive] = useState(false);
  const [, setActiveIndex] = useState(null);
  const [activeSearch, setActiveSearch] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [activeCategory, setActiveCategory] = useState(false);
  const [activeIndexCat, setActiveIndexCat] = useState(null);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [mobileSearchTop, setMobileSearchTop] = useState(0);
  const [activeAccountDropdown, setActiveAccountDropdown] = useState(false);
  const [activeTopDropdown, setActiveTopDropdown] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(() => !!getCustomerToken());
  const bottomHeaderRef = useRef(null);
  const mobileSearchInputRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const [dbCategories, setDbCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);

  const handleLocationChangeRef = useRef(null);

  // ----------------------------
  // Helpers
  // ----------------------------
  const isDesktop = () => window.innerWidth >= 992;

  const buildShopUrl = ({ cat, loc, q, clearCat = false, clearQ = false } = {}) => {
    const next = new URLSearchParams();
    const activeLoc = loc !== undefined ? loc : searchParams.get("loc");
    if (activeLoc) next.set("loc", activeLoc);
    const activeCat = clearCat ? null : (cat !== undefined ? String(cat) : searchParams.get("cat"));
    if (activeCat) next.set("cat", activeCat);
    const activeQ = clearQ ? null : (q !== undefined ? q : searchParams.get("q"));
    if (activeQ) next.set("q", activeQ);
    const qs = next.toString();
    return qs ? `/shop?${qs}` : "/shop";
  };

  const goToShopWithSearch = (qRaw, { replace = true } = {}) => {
    const q = (qRaw || "").trim();
    navigate(buildShopUrl({ q: q || undefined, clearQ: !q }), { replace });
  };

  const onChangeSearch = (val) => {
    setSearchValue(val);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      const v = (val || "").trim();
      if (v.length === 0) { goToShopWithSearch(""); return; }
      if (v.length < 2) return;
      goToShopWithSearch(v);
    }, 350);
  };

  useEffect(() => {
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, []);

  // ── Close mobile menu when clicking outside ──
  useEffect(() => {
    if (!menuActive) return;
    const handleOutsideClick = (e) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) {
        // Don't close if clicked the hamburger toggle
        if (e.target.closest(".toggle-mobileMenu")) return;
        setMenuActive(false);
        setActiveIndex(null);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [menuActive]);

  // ── Prevent body scroll when menu open ──
  useEffect(() => {
    document.body.style.overflow = menuActive ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuActive]);

  // ── Close top mobile dropdowns when clicking outside ──
  useEffect(() => {
    if (!activeTopDropdown) return;

    const closeTopDropdown = (e) => {
      if (e.target.closest(".top-dd")) return;
      setActiveTopDropdown(null);
    };

    document.addEventListener("mousedown", closeTopDropdown);
    document.addEventListener("touchstart", closeTopDropdown);

    return () => {
      document.removeEventListener("mousedown", closeTopDropdown);
      document.removeEventListener("touchstart", closeTopDropdown);
    };
  }, [activeTopDropdown]);

  const closeMenu = useCallback(() => {
    setMenuActive(false);
    setActiveIndex(null);
  }, []);

  const loadCartCount = async () => {
    const token = getCustomerToken();
    if (!token) { setCartCount(0); return; }
    try {
      const items = await CartServices.getCart();
      const totalQty = (items || []).reduce((sum, it) => {
        const q = Number(it?.quantity ?? 1);
        return sum + (Number.isFinite(q) ? q : 1);
      }, 0);
      setCartCount(totalQty);
    } catch (err) {
      console.error("Failed to load cart count:", err);
      setCartCount(0);
    }
  };

  const loadWishlistCount = async () => {
    const token = getCustomerToken();
    if (!token) { setWishlistCount(0); return; }
    try {
      const items = await WishlistServices.getWishlist();
      setWishlistCount(Array.isArray(items) ? items.length : 0);
    } catch (err) {
      console.error("Failed to load wishlist count:", err);
      setWishlistCount(0);
    }
  };

  // ----------------------------
  // Config
  // ----------------------------
  const config = {
    menuData: [
      { id: 0, name: "Home", to: "/" },
      { id: 1, name: "Shop", to: buildShopUrl({}) },
    ],
    contactMenuItem: { to: "/contact", label: "Contact Us" },
    languages: [{ code: "English", flag: "/assets/images/thumbs/flag1.png" }],
    currencies: [{ code: "USD", flag: "/assets/images/thumbs/flag1.png" }],
    locations: locations.length
      ? locations
      : [
          { name: "United States", flag: "/assets/images/thumbs/flag1.png" },
          { name: "Bangladesh", flag: "/assets/images/thumbs/flag6.png" },
        ],
    headerTopLinks: [
      { to: "/live-chat", label: "Live Chat" },
    ],
    headerActions: {
      wishlist: { to: "/wishlist", icon: "ph ph-heart", label: "Wishlist", badgeCount: wishlistCount },
      cart: { to: "/cart", icon: "ph ph-shopping-cart-simple", label: "Cart", badgeCount: cartCount },
      account: { to: "/account", icon: "ph ph-user-circle", label: "Account", badgeCount: null },
    },
    searchForms: {
      middleHeader: {
        inputClass: "search-form__input common-input py-13 ps-20 pe-56 rounded-pill",
        buttonClass: "w-32 h-32",
        buttonTextSize: "text-xl",
      },
    },
    helpCenterOptions: [
      { icon: "ph ph-headset", label: "Call Center", to: "#" },
      { icon: "ph ph-chat-circle-dots", label: "Live Chat", to: "/live-chat" },
    ],
  };

  const categorySelectOptions = useMemo(() => {
    return (dbCategories || []).map((parent) => {
      const children = parent.children || [];
      const options = [{ value: `parent:${parent.id}`, text: parent.name }];
      children.forEach((child) => {
        options.push({ value: `child:${child.id}`, text: `${parent.name} — ${child.name}` });
      });
      return { label: parent.name, options };
    });
  }, [dbCategories]);

  // ----------------------------
  // Effects
  // ----------------------------
  useEffect(() => {
    const handleScroll = () => setScroll(window.pageYOffset > 150);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const loadLocations = async () => {
      try {
        const list = await getLocations();
        if (Array.isArray(list) && list.length) {
          setLocations(list);
          localStorage.setItem("locationsList", JSON.stringify(list));
          const locFromUrl   = searchParams.get("loc");
          const locFromStore = localStorage.getItem("selectedLocation");
          const preferred =
            (locFromUrl && list.some((x) => x?.name === locFromUrl) ? locFromUrl : null) ||
            (locFromStore && list.some((x) => x?.name === locFromStore) ? locFromStore : null) ||
            list[0].name;
          setSelectedLocation(preferred);
          localStorage.setItem("selectedLocation", preferred);
          const selectedLoc = list.find((loc) => loc.name === preferred);
          if (selectedLoc?.id) {
            localStorage.setItem("selectedLocationId", selectedLoc.id.toString());
          }
          if (locFromUrl !== preferred) {
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set("loc", preferred);
              return next;
            }, { replace: true });
          }
        }
      } catch (err) {
        console.error("Failed to load locations", err);
      }
    };
    loadLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showCategories) return;
    const fetchCategoryTree = async () => {
      try {
        setCatLoading(true);
        const data = await CategoriesServices.getCategoryTree();
        setDbCategories(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load category tree", err);
        setDbCategories([]);
      } finally {
        setCatLoading(false);
      }
    };
    fetchCategoryTree();
  }, [showCategories]);

  useEffect(() => {
    const refreshWishlist = () => loadWishlistCount();
    const refreshCart = () => loadCartCount();
    refreshWishlist();
    refreshCart();
    window.addEventListener("wishlist_updated", refreshWishlist);
    window.addEventListener("cart_updated", refreshCart);
    const onAuthChange = () => {
      setIsLoggedIn(!!getCustomerToken());
      refreshCart();
      refreshWishlist();
    };
    window.addEventListener("customer_logged_in", onAuthChange);
    window.addEventListener("customer_logged_out", onAuthChange);
    window.addEventListener("customer_auth_changed", onAuthChange);
    return () => {
      window.removeEventListener("wishlist_updated", refreshWishlist);
      window.removeEventListener("cart_updated", refreshCart);
      window.removeEventListener("customer_logged_in", onAuthChange);
      window.removeEventListener("customer_logged_out", onAuthChange);
      window.removeEventListener("customer_auth_changed", onAuthChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showCategories) return;
    const $select = query(".js-example-basic-single");
    if ($select.data("select2")) $select.select2("destroy");
    $select.select2({ placeholder: "Search category...", allowClear: true, width: "resolve" });
    $select.on("change", function () {
      const selected = query(this).val();
      if (!selected) return;
      const parts = String(selected).split(":");
      const id = parts[1];
      if (id) navigate(buildShopUrl({ cat: id }));
    });
    return () => {
      $select.off("change");
      if ($select.data("select2")) $select.select2("destroy");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categorySelectOptions, navigate, showCategories]);

  useEffect(() => {
    const $locationSelect = query(".js-location-select");
    if ($locationSelect.length === 0) return;
    if ($locationSelect.data("select2")) $locationSelect.select2("destroy");
    $locationSelect.select2({ minimumResultsForSearch: Infinity, width: "resolve" });
    $locationSelect.on("change", function () {
      const selected = query(this).val();
      if (selected && handleLocationChangeRef.current) {
        handleLocationChangeRef.current(selected);
      }
    });
    return () => {
      $locationSelect.off("change");
      if ($locationSelect.data("select2")) $locationSelect.select2("destroy");
    };
  }, []);

  useEffect(() => {
    if (!showSearchBar) { setActiveSearch(false); setSearchValue(""); setMobileSearchOpen(false); }
  }, [showSearchBar]);

  useEffect(() => {
    if (!showCategories) { setActiveCategory(false); setActiveIndexCat(null); }
  }, [showCategories]);

  const calcMobileSearchTop = () => {
    const el = bottomHeaderRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMobileSearchTop(Math.max(0, rect.bottom));
  };

  useEffect(() => {
    if (!mobileSearchOpen) return;
    calcMobileSearchTop();
    const onScroll = () => calcMobileSearchTop();
    const onResize = () => calcMobileSearchTop();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileSearchOpen, scroll]);

  useEffect(() => {
    if (!mobileSearchOpen) return;
    const t = setTimeout(() => mobileSearchInputRef.current?.focus?.(), 120);
    return () => clearTimeout(t);
  }, [mobileSearchOpen]);

  // ----------------------------
  // Handlers
  // ----------------------------
  const handleLanguageChange = (language) => setSelectedLanguage(language);
  const handleCurrencyChange = (currency) => setSelectedCurrency(currency);

  const handleLocationChange = (location) => {
    setSelectedLocation(location);
    localStorage.setItem("selectedLocation", location);
    const locationsList = JSON.parse(localStorage.getItem("locationsList") || "[]");
    const selectedLoc = locationsList.find((loc) => loc.name === location);
    if (selectedLoc?.id) {
      localStorage.setItem("selectedLocationId", selectedLoc.id.toString());
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (location) next.set("loc", location);
      else next.delete("loc");
      return next;
    }, { replace: true });
  };

  handleLocationChangeRef.current = handleLocationChange;

  const handleMenuToggle = () => setMenuActive((prev) => !prev);
  const handleSearchToggle = () => setActiveSearch(!activeSearch);
  const handleCategoryToggle = () => setActiveCategory(!activeCategory);
  const closeCategory = () => setActiveIndexCat(null);

  const toggleTopDropdown = (key, e) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveTopDropdown((current) => (current === key ? null : key));
  };

  const onSubmitSearch = (e) => {
    e.preventDefault();
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    goToShopWithSearch(searchValue, { replace: false });
    setMobileSearchOpen(false);
    setActiveSearch(false);
  };

  const handleLogout = async (e) => {
    e.preventDefault();
    try { await CustomerAuthService.logout(); }
    catch (err) { console.error("Logout error:", err); }
    finally { setActiveAccountDropdown(false); navigate("/"); }
  };

  // ----------------------------
  // UI Helpers
  // ----------------------------
  const renderLogo = (additionalClasses = "") => (
    <div className={`logo ${additionalClasses}`}>
      <Link to="/" className="link">
        <img src="/assets/images/logo/header_logo.svg" alt="Logo" />
      </Link>
    </div>
  );

  const renderDropdownOptions = (items, onClickHandler, extraClass = "", dropdownKey = "") => (
    <ul
      className={`selectable-text-list on-hover-dropdown common-dropdown common-dropdown--sm max-h-200 scroll-sm px-0 py-8 top-dd__menu ${extraClass} ${activeTopDropdown === dropdownKey ? "show" : ""}`}
    >
      {items.map((item, idx) => (
        <li key={idx}>
          <Link
            to="#"
            className="hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0"
            onClick={(e) => {
              e.preventDefault();
              onClickHandler(item.code);
              setActiveTopDropdown(null);
            }}
          >
            <img
              src={item.flag}
              alt=""
              className="w-16 h-12 rounded-4 border border-gray-100"
            />
            {item.code}
          </Link>
        </li>
      ))}
    </ul>
  );

  const renderHeaderActionItem = (type) => {
    const item = config.headerActions[type];
    if (type === "account") {
      return (
        <div
          key={type}
          className="on-hover-item has-submenu arrow-white position-relative"
          onMouseEnter={() => { if (isDesktop()) setActiveAccountDropdown(true); }}
          onMouseLeave={() => { if (isDesktop()) setActiveAccountDropdown(false); }}
        >
          <Link
            to="#"
            className="flex-align gap-4 item-hover"
            onClick={(e) => { e.preventDefault(); setActiveAccountDropdown((p) => !p); }}
          >
            <span className="text-2xl text-gray-700 d-flex position-relative me-6 mt-6 item-hover__text">
              <i className={item.icon} />
            </span>
            <span className="text-md text-gray-500 item-hover__text d-none d-lg-flex">{item.label}</span>
          </Link>
          <ul
            className={`on-hover-dropdown common-dropdown common-dropdown--sm max-h-200 scroll-sm px-0 py-8 dropdown-align-right ${activeAccountDropdown ? "show" : ""}`}
          >
            {isLoggedIn ? (
              <>
                <li>
                  <Link to="/account" className="hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0" onClick={() => setActiveAccountDropdown(false)}>
                    <i className="ph ph-user" /> Profile
                  </Link>
                </li>
                <li>
                  <Link to="#" className="hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0" onClick={handleLogout}>
                    <i className="ph ph-sign-out" /> Logout
                  </Link>
                </li>
              </>
            ) : (
              <>
                <li>
                  <Link to="/login" className="hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0" onClick={() => setActiveAccountDropdown(false)}>
                    <i className="ph ph-sign-in" /> Login
                  </Link>
                </li>
                <li>
                  <Link to="/signup" className="hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0" onClick={() => setActiveAccountDropdown(false)}>
                    <i className="ph ph-user-plus" /> Signup
                  </Link>
                </li>
              </>
            )}
          </ul>
        </div>
      );
    }
    return (
      <Link key={type} to={item.to} className="flex-align gap-4 item-hover">
        <span className="text-2xl text-gray-700 d-flex position-relative me-6 mt-6 item-hover__text">
          <i className={item.icon} />
          {item.badgeCount !== null && item.badgeCount > 0 && (
            <span className="w-16 h-16 flex-center rounded-circle bg-main-600 text-white text-xs position-absolute top-n6 end-n4">
              {item.badgeCount}
            </span>
          )}
        </span>
        <span className="text-md text-gray-500 item-hover__text d-none d-lg-flex">{item.label}</span>
      </Link>
    );
  };

  const renderSearchForm = (type, placeholder = "Search for a product or brand") => {
    const formConfig = config.searchForms[type];
    return (
      <form action="#" className="position-relative" onSubmit={onSubmitSearch}>
        <input
          type="text"
          className={formConfig.inputClass}
          placeholder={placeholder}
          value={searchValue}
          onChange={(e) => onChangeSearch(e.target.value)}
        />
        <button
          type="submit"
          className={`${formConfig.buttonClass} bg-main-600 rounded-circle flex-center ${formConfig.buttonTextSize} text-white position-absolute top-50 translate-middle-y inset-inline-end-0 me-10`}
          aria-label="Search"
        >
          <i className="ph ph-magnifying-glass" />
        </button>
      </form>
    );
  };

  // ── Compact location box — replaces the oversized pill ──
  const renderLocationBox = () => {
    const currentLocation = config.locations.find((loc) => loc.name === selectedLocation) || config.locations[0];
    return (
      <>
        <style>{`
          /* ── Location box: compact & professional ── */
          .loc-box {
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 6px 10px 6px 8px;
            border-radius: 8px;
            border: 1px solid rgba(0,0,0,0.10);
            background: #fff;
            cursor: pointer;
            transition: border-color 0.2s, box-shadow 0.2s;
            min-width: 0;
            flex-shrink: 0;
          }
          .loc-box:hover {
            border-color: rgba(29,158,92,0.40);
            box-shadow: 0 2px 8px rgba(29,158,92,0.08);
          }
          .loc-box__icon {
            font-size: 15px;
            color: #1d9e5c;
            flex-shrink: 0;
            line-height: 1;
          }
          .loc-box__inner {
            display: flex;
            flex-direction: column;
            gap: 1px;
            min-width: 0;
          }
          .loc-box__label {
            font-size: 9px;
            font-weight: 700;
            letter-spacing: 0.7px;
            text-transform: uppercase;
            color: #9ca3af;
            line-height: 1;
            white-space: nowrap;
          }
          .loc-box__row {
            display: flex;
            align-items: center;
            gap: 5px;
          }
          .loc-box__flag {
            width: 14px;
            height: 10px;
            border-radius: 2px;
            border: 1px solid rgba(0,0,0,0.08);
            flex-shrink: 0;
            object-fit: cover;
          }
          .loc-box__select {
            border: none;
            background: transparent;
            font-size: 12px;
            font-weight: 500;
            color: #111827;
            cursor: pointer;
            outline: none;
            padding: 0;
            max-width: 110px;
            line-height: 1.3;
          }
          /* Mobile: even more compact */
          @media (max-width: 575px) {
            .loc-box {
              padding: 5px 8px 5px 7px;
              border-radius: 6px;
            }
            .loc-box__icon {
              font-size: 13px;
            }
            .loc-box__select {
              font-size: 11px;
              max-width: 80px;
            }
            .loc-box__label {
              font-size: 8px;
            }
          }

          /* ── Mobile menu backdrop & panel ── */
          .mm-backdrop {
            display: none;
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,0.40);
            z-index: 1098;
            opacity: 0;
            transition: opacity 0.25s ease;
          }
          .mm-backdrop--active {
            display: block;
            opacity: 1;
          }

          /* ── Mobile menu: slide from left, proper sizing ── */
          .mobile-menu {
            position: fixed;
            inset-block: 0;
            inset-inline-start: 0;
            width: 280px;
            max-width: 85vw;
            z-index: 1099;
            transform: translateX(-100%);
            transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
            background: #fff;
            box-shadow: 4px 0 24px rgba(0,0,0,0.10);
            display: flex;
            flex-direction: column;
            overflow: hidden;
          }
          .mobile-menu.active {
            transform: translateX(0);
          }

          .mm-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 14px 16px;
            border-bottom: 1px solid rgba(0,0,0,0.06);
            flex-shrink: 0;
          }
          .mm-close-btn {
            width: 32px;
            height: 32px;
            border-radius: 8px;
            border: 1px solid rgba(0,0,0,0.08);
            background: #f9fafb;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            font-size: 18px;
            color: #374151;
            flex-shrink: 0;
            transition: background 0.15s;
          }
          .mm-close-btn:hover {
            background: #f3f4f6;
          }

          .mm-location {
            padding: 10px 16px;
            border-bottom: 1px solid rgba(0,0,0,0.05);
            background: #f9fafb;
            flex-shrink: 0;
          }
          .mm-location-label {
            font-size: 9px;
            font-weight: 700;
            letter-spacing: 0.7px;
            text-transform: uppercase;
            color: #9ca3af;
            margin-bottom: 5px;
          }
          .mm-location-row {
            display: flex;
            align-items: center;
            gap: 7px;
          }
          .mm-location-flag {
            width: 18px;
            height: 13px;
            border-radius: 3px;
            border: 1px solid rgba(0,0,0,0.08);
            object-fit: cover;
            flex-shrink: 0;
          }
          .mm-location-select {
            border: 1px solid rgba(0,0,0,0.10);
            border-radius: 6px;
            background: #fff;
            padding: 4px 8px;
            font-size: 12px;
            font-weight: 500;
            color: #111827;
            outline: none;
            flex: 1;
            min-width: 0;
          }

          .mm-nav {
            flex: 1;
            overflow-y: auto;
            padding: 8px 0;
          }
          .mm-nav-item {
            display: block;
            padding: 11px 20px;
            font-size: 13.5px;
            font-weight: 500;
            color: #374151;
            text-decoration: none;
            border-bottom: 1px solid rgba(0,0,0,0.04);
            transition: background 0.15s, color 0.15s;
            display: flex;
            align-items: center;
            gap: 10px;
          }
          .mm-nav-item:hover,
          .mm-nav-item:active {
            background: rgba(29,158,92,0.05);
            color: #1d9e5c;
          }
          .mm-nav-item i {
            font-size: 17px;
            color: #6b7280;
            flex-shrink: 0;
            transition: color 0.15s;
          }
          .mm-nav-item:hover i {
            color: #1d9e5c;
          }

          .mm-footer {
            border-top: 1px solid rgba(0,0,0,0.06);
            padding: 12px 16px;
            flex-shrink: 0;
            display: flex;
            gap: 8px;
          }
          .mm-footer-btn {
            flex: 1;
            padding: 8px 10px;
            border-radius: 8px;
            font-size: 12px;
            font-weight: 600;
            text-align: center;
            text-decoration: none;
            transition: background 0.15s;
          }
          .mm-footer-btn--outline {
            border: 1px solid rgba(0,0,0,0.12);
            color: #374151;
            background: #fff;
          }
          .mm-footer-btn--outline:hover {
            background: #f3f4f6;
          }
          .mm-footer-btn--fill {
            border: 1px solid #1d9e5c;
            color: #fff;
            background: #1d9e5c;
          }
          .mm-footer-btn--fill:hover {
            background: #18885000;
            color: #1d9e5c;
          }
          .dropdown-align-right {
            left: auto !important;
            right: 0 !important;
            transform: none !important;
          }

          .header-top__right .on-hover-item,
          .header-right .on-hover-item {
            position: relative;
          }

          .header-top__right .common-dropdown,
          .header-right .common-dropdown {
            min-width: 150px;
            max-width: 220px;
            white-space: nowrap;
          }

          /* Top mobile dropdown fix: each panel stays under its own trigger */
          .header-top__right .top-dd {
            position: relative !important;
          }
          .header-top__right .top-dd__menu {
            top: calc(100% + 2px) !important;
            transform: none;
            z-index: 1200;
          }
          .header-top__right .top-dd--help .top-dd__menu {
            left: 0 !important;
            right: auto !important;
          }
          .header-top__right .top-dd--language .top-dd__menu {
            left: 50% !important;
            right: auto !important;
            transform: translateX(-50%) !important;
          }
          .header-top__right .top-dd--currency .top-dd__menu {
            left: auto !important;
            right: 0 !important;
          }

          @media (max-width: 991px) {
            .header-top__right .top-dd .top-dd__menu {
              display: none !important;
              opacity: 0 !important;
              visibility: hidden !important;
              pointer-events: none !important;
              min-width: 150px;
              max-width: calc(100vw - 16px);
              white-space: nowrap;
            }

            .header-top__right .top-dd.top-dd--open .top-dd__menu {
              display: block !important;
              opacity: 1 !important;
              visibility: visible !important;
              pointer-events: auto !important;
            }
          }

          @media (max-width: 380px) {
            .header-top__right .top-dd--language .top-dd__menu {
              left: auto !important;
              right: 0 !important;
              transform: none !important;
            }
          }
        `}</style>
        <div className="loc-box">
          <i className="ph ph-map-pin loc-box__icon" />
          <div className="loc-box__inner">
            <span className="loc-box__label">Location</span>
            <div className="loc-box__row">
              <img
                src={currentLocation?.flag}
                alt={currentLocation?.name}
                className="loc-box__flag"
              />
              <select
                value={selectedLocation}
                onChange={(e) => handleLocationChange(e.target.value)}
                className="loc-box__select"
                name="location"
                aria-label="Select location"
              >
                {config.locations.map((location) => (
                  <option key={location.name} value={location.name}>{location.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderMenuItem = (item, isMobile = false) => {
    const resolvedTo = item.to === null ? buildShopUrl({}) : item.to;
    if (!resolvedTo) return null;

    if (isMobile) {
      return (
        <li key={item.id} className="nav-menu__item">
          <Link
            to={resolvedTo}
            className="nav-menu__link"
            onClick={closeMenu}  // ✅ closes sidebar on click
          >
            {item.name}
          </Link>
        </li>
      );
    }
    return (
      <li key={item.id} className="nav-menu__item">
        <NavLink
          to={resolvedTo}
          className={(navData) =>
            navData.isActive ? "nav-menu__link activePage" : "nav-menu__link"
          }
        >
          {item.name}
        </NavLink>
      </li>
    );
  };

  const renderCategoryItem = (category) => {
    const children = category.children || [];
    const hasChildren = children.length > 0;
    const isOpen = activeIndexCat === category.id;

    const parentHref = category?.id
      ? buildShopUrl({ cat: category.id })
      : buildShopUrl({ clearCat: true });

    return (
      <li
        key={category.id}
        className={`has-submenus-submenu ${isOpen ? "active" : ""}`}
        onMouseEnter={() => { if (isDesktop()) setActiveIndexCat(category.id); }}
        onClick={() => {
          if (!isDesktop() && hasChildren) {
            setActiveIndexCat(isOpen ? null : category.id);
          }
        }}
      >
        <Link
          to={parentHref}
          className="text-gray-500 text-15 py-12 px-16 flex-align gap-8 rounded-0"
          onClick={(e) => {
            if (isDesktop()) { setActiveCategory(false); setActiveIndexCat(null); return; }
            if (!isDesktop() && hasChildren) {
              e.preventDefault();
              setActiveIndexCat(isOpen ? null : category.id);
            } else {
              setActiveCategory(false);
              setActiveIndexCat(null);
            }
          }}
        >
          <span className="text-xl d-flex"><i className="ph ph-dots-nine" /></span>
          <span>{category.name}</span>
          {hasChildren && (
            <span className="icon text-md d-flex ms-auto"><i className="ph ph-caret-right" /></span>
          )}
        </Link>

        {hasChildren && (
          <div className={`submenus-submenu py-16 ${isOpen ? "open" : ""}`}>
            <h6 className="text-lg px-16 submenus-submenu__title">{category.name}</h6>
            <ul className="submenus-submenu__list max-h-300 overflow-y-auto scroll-sm">
              <li className="px-16 mb-8 d-lg-none d-block">
                <Link
                  to={parentHref}
                  className="d-inline-flex align-items-center gap-6 text-main-600 fw-semibold"
                  onClick={() => { setActiveCategory(false); setActiveIndexCat(null); }}
                >
                  View all {category.name} <i className="ph ph-arrow-right" />
                </Link>
              </li>
              {children.map((child) => (
                <li key={child.id}>
                  <Link
                    to={buildShopUrl({ cat: child.id })}
                    onClick={() => { setActiveCategory(false); setActiveIndexCat(null); }}
                  >
                    {child.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </li>
    );
  };

  // ----------------------------
  // Render
  // ----------------------------
  return (
    <>
      <div className="overlay" />
      <div className={`side-overlay ${(menuActive || activeCategory) && "show"}`} />

      {/* ── Mobile menu backdrop (outside click closes menu) ── */}
      <div
        className={`mm-backdrop ${menuActive ? "mm-backdrop--active" : ""}`}
        onClick={closeMenu}
        aria-hidden="true"
      />

      {/* Search Overlay */}
      {showSearchBar && (
        <form action="#" className={`search-box ${activeSearch && "active"}`} onSubmit={onSubmitSearch}>
          <button
            onClick={handleSearchToggle}
            type="button"
            className="search-box__close position-absolute inset-block-start-0 inset-inline-end-0 m-16 w-48 h-48 border border-gray-100 rounded-circle flex-center text-white hover-text-gray-800 hover-bg-white text-2xl transition-1"
          >
            <i className="ph ph-x" />
          </button>
          <div className="container">
            <div className="position-relative">
              <input
                type="text"
                className="form-control py-16 px-24 text-xl rounded-pill pe-64"
                placeholder="Search for a product or brand"
                value={searchValue}
                onChange={(e) => onChangeSearch(e.target.value)}
                autoFocus={activeSearch}
              />
              <button type="submit" className="w-48 h-48 bg-main-600 rounded-circle flex-center text-xl text-white position-absolute top-50 translate-middle-y inset-inline-end-0 me-8" aria-label="Search">
                <i className="ph ph-magnifying-glass" />
              </button>
            </div>
          </div>
        </form>
      )}

      {/* ── Mobile Menu (rebuilt for responsiveness + auto-close) ── */}
      <div
        ref={mobileMenuRef}
        className={`mobile-menu d-lg-none ${menuActive ? "active" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        {/* Header row */}
        <div className="mm-header">
          <Link to="/" onClick={closeMenu}>
            <img src="/assets/images/logo/header_logo.svg" alt="Logo" style={{ height: 28, width: "auto" }} />
          </Link>
          <button className="mm-close-btn" onClick={closeMenu} aria-label="Close menu">
            <i className="ph ph-x" />
          </button>
        </div>

        {/* Nav links — each one closes the menu on click */}
        <nav className="mm-nav">
          <Link to="/" className="mm-nav-item" onClick={closeMenu}>
            <i className="ph ph-house" /> Home
          </Link>
          <Link to={buildShopUrl({})} className="mm-nav-item" onClick={closeMenu}>
            <i className="ph ph-storefront" /> Shop
          </Link>
          <Link to="/about" className="mm-nav-item" onClick={closeMenu}>
            <i className="ph ph-info" /> About Us
          </Link>
          <Link to="/contact" className="mm-nav-item" onClick={closeMenu}>
            <i className="ph ph-envelope" /> Contact Us
          </Link>
        </nav>
      </div>

      {/* Header Top */}
      <div className="header-top bg-main-600 flex-between">
        <div className="container container-lg">
          <div className="flex-between flex-wrap gap-8">
            <ul className="flex-align flex-wrap d-none d-md-flex">
              {config.headerTopLinks.map((link, idx) => (
                <li key={idx} className="border-right-item">
                  <Link to={link.to} className="text-white text-sm hover-text-decoration-underline">{link.label}</Link>
                </li>
              ))}
            </ul>
            <ul className="header-top__right flex-align flex-wrap">
              <li className={`on-hover-item border-right-item border-right-item-sm-space has-submenu arrow-white d-lg-none top-dd top-dd--help ${activeTopDropdown === "help" ? "top-dd--open" : ""}`}>
                <Link
                  to="#"
                  className="text-white text-sm py-8"
                  onClick={(e) => toggleTopDropdown("help", e)}
                  aria-expanded={activeTopDropdown === "help"}
                >
                  Help Center
                </Link>
                <ul className={`on-hover-dropdown common-dropdown common-dropdown--sm max-h-200 scroll-sm px-0 py-8 top-dd__menu ${activeTopDropdown === "help" ? "show" : ""}`}>
                  {config.helpCenterOptions.map((option, idx) => (
                    <li key={idx} className="nav-submenu__item">
                      <Link
                        to={option.to}
                        className="nav-submenu__link hover-bg-gray-100 text-gray-500 text-xs py-6 px-16 flex-align gap-8 rounded-0"
                        onClick={() => setActiveTopDropdown(null)}
                      >
                        <span className="text-sm d-flex"><i className={option.icon} /></span>
                        {option.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
              <li className={`on-hover-item border-right-item border-right-item-sm-space has-submenu arrow-white top-dd top-dd--language ${activeTopDropdown === "language" ? "top-dd--open" : ""}`}>
                <Link
                  to="#"
                  className="selected-text text-white text-sm py-8"
                  onClick={(e) => toggleTopDropdown("language", e)}
                  aria-expanded={activeTopDropdown === "language"}
                >
                  {selectedLanguage}
                </Link>
                {renderDropdownOptions(config.languages, handleLanguageChange, "", "language")}
              </li>
              <li className={`on-hover-item border-right-item border-right-item-sm-space has-submenu arrow-white top-dd top-dd--currency ${activeTopDropdown === "currency" ? "top-dd--open" : ""}`}>
                <Link
                  to="#"
                  className="selected-text text-white text-sm py-8"
                  onClick={(e) => toggleTopDropdown("currency", e)}
                  aria-expanded={activeTopDropdown === "currency"}
                >
                  {selectedCurrency}
                </Link>
                {renderDropdownOptions(config.currencies, handleCurrencyChange, "", "currency")}
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Middle Header */}
      <header className="header-middle bg-color-one border-bottom border-gray-100">
        <div className="container container-lg">
          <nav className="header-inner flex-between">
            {renderLogo()}
            {showSearchBar && (
              <div className="flex-align flex-wrap form-location-wrapper ms-auto me-12">
                <div className="search-category d-flex h-48 select-border-end-0 radius-end-0 search-form d-sm-flex d-none">
                  <div className="search-form__wrapper position-relative">
                    {renderSearchForm("middleHeader")}
                  </div>
                </div>
                {renderLocationBox()}
              </div>
            )}
            <div className="header-right flex-align d-lg-block d-none">
              <div className="flex-align flex-wrap gap-12">
                {Object.keys(config.headerActions).map((type) => renderHeaderActionItem(type))}
              </div>
            </div>
            <button
              onClick={handleMenuToggle}
              type="button"
              className="toggle-mobileMenu d-lg-none ms-3n text-gray-800 text-4xl d-flex"
              aria-label={menuActive ? "Close menu" : "Open menu"}
              aria-expanded={menuActive}
            >
              <i className={`ph ${menuActive ? "ph-x" : "ph-list"}`} />
            </button>
          </nav>
        </div>
      </header>

      {/* Main Header */}
      <header ref={bottomHeaderRef} className={`header bg-white border-bottom border-gray-100 ${scroll && "fixed-header"}`}>
        <div className="container container-lg">
          <nav className="header-inner d-flex justify-content-between gap-8">
            <div className="flex-align menu-category-wrapper">
              {showCategories && (
                <div className="category on-hover-item">
                  <button
                    onClick={handleCategoryToggle}
                    type="button"
                    className="category__button flex-align gap-8 fw-medium p-16 border-end border-start border-gray-100 text-heading"
                  >
                    <span className="icon text-2xl d-xs-flex d-none"><i className="ph ph-dots-nine" /></span>
                    <span className="d-sm-flex d-none">All</span> Categories
                    <span className="arrow-icon text-xl d-flex"><i className="ph ph-caret-down" /></span>
                  </button>
                  <div
                    className={`responsive-dropdown cat on-hover-dropdown common-dropdown nav-submenu p-0 submenus-submenu-wrapper ${activeCategory && "active"}`}
                    onMouseLeave={() => closeCategory()}
                  >
                    <button
                      onClick={() => { handleCategoryToggle(); setActiveIndexCat(null); }}
                      type="button"
                      className="close-responsive-dropdown rounded-circle text-xl position-absolute inset-inline-end-0 inset-block-start-0 mt-4 me-8 d-lg-none d-flex"
                    >
                      <i className="ph ph-x" />
                    </button>
                    <div className="logo px-16 d-lg-none d-block">
                      <Link to="/" className="link">
                        <img src="/assets/images/logo/header_logo.svg" alt="Logo" />
                      </Link>
                    </div>
                    <div className="submenus-submenu-wrapper-inner">
                      <ul className="scroll-sm p-0 py-8 w-300 max-h-400 overflow-y-auto">
                        {catLoading ? (
                          <li className="px-16 py-12 text-sm text-gray-400">Loading categories...</li>
                        ) : dbCategories.length ? (
                          dbCategories.map((category) => renderCategoryItem(category))
                        ) : (
                          <li className="px-16 py-12 text-sm text-gray-400">No categories found</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              <div className="header-menu d-lg-block d-none">
                <ul className="nav-menu flex-align">
                  {config.menuData.map((item) => renderMenuItem(item, false))}
                  <li className="nav-menu__item">
                    <NavLink to={config.contactMenuItem.to} className={(navData) => (navData.isActive ? "nav-menu__link activePage" : "nav-menu__link")}>
                      {config.contactMenuItem.label}
                    </NavLink>
                  </li>
                </ul>
              </div>
            </div>

            {/* Right mobile actions */}
            <div className="header-right flex-align">
              <div className="me-16 d-lg-none d-block">
                <div className="flex-align gap-12">
                  {showSearchBar && (
                    <button
                      type="button"
                      onClick={() => { calcMobileSearchTop(); setMobileSearchOpen((p) => !p); }}
                      className="flex-align gap-4 item-hover bg-transparent border-0 p-0"
                      aria-label={mobileSearchOpen ? "Close search" : "Open search"}
                    >
                      <span className="text-2xl text-gray-700 d-flex position-relative me-6 mt-6 item-hover__text">
                        <i className={`ph ${mobileSearchOpen ? "ph-x" : "ph-magnifying-glass"}`} />
                      </span>
                    </button>
                  )}
                  {Object.keys(config.headerActions).map((type) => renderHeaderActionItem(type))}
                </div>
              </div>
            </div>
          </nav>
        </div>
      </header>

      {/* Mobile search bar */}
      {showSearchBar && (
        <div
          className="d-lg-none"
          style={{ position: "fixed", left: 0, right: 0, top: mobileSearchTop, zIndex: 9999, pointerEvents: mobileSearchOpen ? "auto" : "none" }}
        >
          <div
            style={{
              maxHeight: mobileSearchOpen ? 90 : 0,
              opacity: mobileSearchOpen ? 1 : 0,
              overflow: "hidden",
              transition: "max-height 260ms ease, opacity 220ms ease",
              willChange: "max-height, opacity",
              background: "#fff",
              borderBottom: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <div className="container container-lg" style={{ paddingTop: 10, paddingBottom: 12 }}>
              <form onSubmit={onSubmitSearch} className="position-relative" style={{ width: "100%" }}>
                <input
                  ref={mobileSearchInputRef}
                  type="text"
                  className="common-input py-13 ps-20 pe-56 rounded-pill"
                  placeholder="Search for a product or brand"
                  value={searchValue}
                  onChange={(e) => onChangeSearch(e.target.value)}
                  style={{ width: "100%" }}
                />
                <button type="submit" className="w-32 h-32 bg-main-600 rounded-circle flex-center text-xl text-white position-absolute top-50 translate-middle-y inset-inline-end-0 me-10" aria-label="Search">
                  <i className="ph ph-magnifying-glass" />
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HeaderOne;